import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { BrowserRouter, Route, Switch } from 'react-router-dom';
import Autosuggest from 'react-autosuggest';
import { Icon, IconButton, Spinner } from 'components/ui';
import useFetch from 'components/shared/hooks/useFetch';
import { routes } from 'constants/config';
import request from 'utils/request_ajax';
import Skeleton from 'ui/Skeleton';
import { debounce } from 'lodash';
import Text from 'components/shared/Text';
import { TEXT_VARIANTS } from 'components/ui/Typography';
import { AlertCircle } from 'react-feather/dist';

const AddEvaluators = ({ groupId, managers }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedEvaluators, setSelectedEvaluators] = useState([]);
  const [suggestions, setSuggestions] = useState([]);
  const [addingEvaluator, setAddingEvaluator] = useState(false);
  const [showError, setShowError] = useState(false);
  const [removedId, setRemovedId] = useState('');
  const [currentEvaluatorIds, setCurrentEvaluatorIds] = useState([]);

  const { data, runRequest, loading, initialLoading } = useFetch(
    routes.GROUP_EVALUATORS(groupId),
  );
  const url = routes.PEOPLE;

  useEffect(() => {
    if (data) {
      setSelectedEvaluators(data);
    }
    setCurrentEvaluatorIds(selectedEvaluators.map(evaluator => evaluator.id));
  }, [data, selectedEvaluators]);

  const onSuggestionsFetchRequested = useCallback(
    ({ value }) => {
      const params = {
        query: value,
        'filter[type][]': 'User',
        'sort[order]': 'ASC',
        'sort[type]': 'name',
        // eslint-disable-next-line camelcase
        per_page: 100,
      };
      setShowError(false);
      setRemovedId('');
      request.get(url, params).then(res => {
        setSuggestions(
          res.data.content
            .filter(
              user =>
                !managers.includes(user.id) &&
                !currentEvaluatorIds.includes(user.id),
            )
            .slice(0, 9),
        );
      });
    },
    [currentEvaluatorIds, managers, url],
  );

  const debouncedFetchSuggestions = useMemo(
    () => debounce(onSuggestionsFetchRequested, 500),
    [onSuggestionsFetchRequested],
  );

  const onSuggestionsClearRequested = () => {
    setShowError(false);
    setSuggestions([]);
    setRemovedId('');
  };
  const onChange = (_e, { newValue }) => {
    setShowError(false);
    setSearchTerm(newValue);
    setRemovedId('');
  };

  function createEvaluator(evaluatorId) {
    setShowError(false);
    setAddingEvaluator(true);
    setRemovedId('');
    request
      .postJSON(routes.GROUP_EVALUATORS(groupId), {
        evaluator_id: evaluatorId,
      })
      .then(result => {
        if (result.success === true) {
          runRequest();
        }
      })
      .catch(() => {
        setShowError(true);
      });
  }

  const onSuggestionSelected = (_e, { suggestion }) => {
    setShowError(false);
    createEvaluator(suggestion.id);
    setSearchTerm('');
  };

  function removeEvaluator(evaluatorId) {
    setShowError(false);
    setAddingEvaluator(false);
    setRemovedId(evaluatorId);
    request
      .delete(routes.DELETE_GROUP_EVALUATOR(groupId, evaluatorId))
      .then(result => {
        if (result.success === true) {
          runRequest();
        }
      })
      .catch(() => {
        setShowError(true);
      });
  }

  const ErrorText = ({ error }) => {
    return (
      <div className="mt-8 mb-8">
        <Text
          className="d-f ai-c"
          variant={TEXT_VARIANTS.INSTRUX_SMALL_SEMIBOLD_ERROR}
        >
          <AlertCircle className="mr-4" size={14} />
          {t('groups.show.evaluators.error')}
        </Text>
      </div>
    );
  };

  if (initialLoading) {
    return (
      <div className="Autocomplete ui-front">
        <div>
          <h3 className="Module-heading SectionHeading">
            <Skeleton />
          </h3>
          <h3 className="SidebarModule-padded">
            <Skeleton />
          </h3>
          <h3 className="SidebarModule-padded">
            <Skeleton />
          </h3>
          <h3 className="quicklist-item">
            <Skeleton />
          </h3>
        </div>
      </div>
    );
  }

  return (
    <div className="Autocomplete ui-front">
      <div>
        <h3 className="Module-heading SectionHeading">
          {t('groups.show.evaluators.title')}
          <span
            className="Module-headingIcon"
            data-toggle="tooltip"
            title=""
            data-original-title={t('groups.show.evaluators.tooltip')}
          >
            <Icon name="info" />
          </span>
        </h3>
        <div className="SidebarModule-padded disabled fs-i pb-8">
          {t('groups.show.evaluators.read_only')}
        </div>
      </div>
      <Autosuggest
        suggestions={suggestions}
        onSuggestionsFetchRequested={debouncedFetchSuggestions}
        onSuggestionsClearRequested={onSuggestionsClearRequested}
        onSuggestionSelected={onSuggestionSelected}
        getSuggestionValue={suggestion => suggestion.name}
        renderSuggestion={suggestion => suggestion.name}
        className="w-fill"
        inputProps={{
          placeholder: t('groups.show.evaluators.select'),
          value: searchTerm,
          onChange,
          type: 'search',
          disabled: false,
          className: 'SidebarModule-input ui-autocomplete-input',
        }}
      />
      <ul>
        {selectedEvaluators.map(evaluator => (
          <li className="quicklist-item" key={evaluator.id}>
            {evaluator.name}
            <IconButton
              className="quicklist-remove h-cr-p p-0"
              type="button"
              onClick={() => removeEvaluator(evaluator.id)}
              onKeyDown={event => {
                if (event.key === 'Enter') {
                  removeEvaluator(evaluator.id);
                }
              }}
            >
              {loading && evaluator.id === removedId ? (
                <Spinner className="mr-10" />
              ) : (
                <Icon name="remove" data-method="delete" />
              )}
            </IconButton>
          </li>
        ))}
        {loading && addingEvaluator && (
          <div className="Autocomplete ui-front pt-12">
            <h3 className="Module-heading SectionHeading">
              <Skeleton />
            </h3>
          </div>
        )}
      </ul>
      {showError && <ErrorText />}
    </div>
  );
};

const AddEvaluatorsRoot = props => {
  const { groupId } = props;
  return (
    <BrowserRouter>
      <Switch>
        <Route
          path="/groups/:groupId"
          exact
          render={() => <AddEvaluators groupId={groupId} {...props} />}
        />
      </Switch>
    </BrowserRouter>
  );
};

export default props => <AddEvaluatorsRoot {...props} />;
